import { type HttpInterceptorFn, HttpRequest, type HttpHandlerFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';

import { AuthService } from '../services/auth.service';
import { LOGIN_ROUTE_POLICY } from '../config/login-route-policy.token';

/**
 * URL fragments that should NOT trigger a token-refresh cycle when they
 * return 401 — doing so would cause infinite recursion or redirect a user
 * away from a public flow (e.g. forgot-password) that must surface its own
 * error instead.
 */
const REFRESH_URL_FRAGMENTS = ['/auth/refresh', '/auth/login', '/auth/forgot-password'];

function isRefreshUrl(url: string): boolean {
  return REFRESH_URL_FRAGMENTS.some((fragment) => url.includes(fragment));
}

export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const loginRoute = inject(LOGIN_ROUTE_POLICY).loginRoute;
  const token = authService.getToken();

  let authReq = req;
  if (token) {
    authReq = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && token && !isRefreshUrl(req.url)) {
        const refreshToken = authService.getRefreshToken();
        const hasValidRefresh = !!refreshToken && refreshToken !== 'undefined' && refreshToken !== 'null';

        // No valid refresh token → skip refresh cycle, just logout
        if (!hasValidRefresh) {
          authService.logout();
          router.navigate([loginRoute]);
          return throwError(() => error);
        }

        return authService.refreshToken().pipe(
          switchMap(() => {
            const newToken = authService.getToken();
            const retryReq = req.clone({
              setHeaders: {
                Authorization: `Bearer ${newToken}`,
              },
            });
            return next(retryReq);
          }),
          catchError((refreshError) => {
            authService.logout();
            router.navigate([loginRoute]);
            return throwError(() => refreshError);
          }),
        );
      }
      return throwError(() => error);
    }),
  );
};
